import { computed } from '@vue/composition-api'
import { useGetStockItemsWithCustomerPrice } from '../services/getStockItemsWithCustomerPriceService'

/**
 * Composable to fetch and format stock items with customer prices for select options
 * @param {Ref<String>|String} companyId - Company ID (can be ref or string)
 * @param {Ref<String>|String} customerId - Customer ID (can be ref or string)
 * @param {Ref<Array>|Array} existingPrices - Array of existing special prices to filter out
 */
export const useStockItemsWithCustomerPrice = (
	companyId,
	customerId,
	existingPrices = [],
) => {
	// Use the service to fetch stock items with customer prices
	const {
		stockItemsWithPrices,
		customerPriceClass,
		loading,
		error,
		executeQuery,
		refetch,
	} = useGetStockItemsWithCustomerPrice(companyId, customerId)

	// Format price for display (assuming prices are stored in cents/minor units)
	const formatPrice = (price) => {
		if (!price && price !== 0) return '0.00'
		return new Intl.NumberFormat('sv-SE', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price / 100)
	}

	// Format stock items for select options
	const formattedStockOptions = computed(() => {
		if (!stockItemsWithPrices.value) return []

		// Get list of existing stock IDs to filter out
		const existingStockIds = new Set()
		const existing = existingPrices.value || existingPrices || []
		existing.forEach((price) => {
			if (price.stock && price.stock.stock_id) {
				existingStockIds.add(price.stock.stock_id)
			}
		})

		// Map and filter stock items
		return stockItemsWithPrices.value
			.filter((stock) => !existingStockIds.has(stock.stock_id))
			.map((stock) => ({
				// Original data
				stock_id: stock.stock_id,
				stock_name: stock.stock_name,
				stock_price_a: stock.stock_price_a,
				stock_price_b: stock.stock_price_b,
				stock_price_c: stock.stock_price_c,
				stock_price_d: stock.stock_price_d,
				customerPrice: stock.customerPrice,
				customerPriceClass: stock.customerPriceClass,
				// Formatted display text for select option
				displayText: `${stock.stock_id} - ${stock.stock_name} - ${formatPrice(
					stock.customerPrice,
				)}`,
				// Formatted price string
				formattedPrice: formatPrice(stock.customerPrice),
			}))
			.sort((a, b) => {
				// Sort by stock_id for consistent ordering
				return a.stock_id.localeCompare(b.stock_id)
			})
	})

	// Get a single stock by ID
	const getStockById = (stockId) => {
		return formattedStockOptions.value.find(
			(stock) => stock.stock_id === stockId,
		)
	}

	// Check if there are available stocks to add
	const hasAvailableStocks = computed(() => {
		return formattedStockOptions.value.length > 0
	})

	// Message when no stocks are available
	const noStocksMessage = computed(() => {
		if (loading.value) return 'Loading stock items...'
		if (error.value) return 'Error loading stock items'
		if (!hasAvailableStocks.value) {
			const existing = existingPrices.value || existingPrices || []
			if (existing.length > 0) {
				return 'All stock items already have special prices'
			}
			return 'No stock items available'
		}
		return ''
	})

	// Refresh the stock list
	const refreshStocks = async () => {
		try {
			await refetch()
		} catch (err) {
			console.error('Error refreshing stock items:', err)
		}
	}

	return {
		// Main data
		formattedStockOptions,
		customerPriceClass,
		// Loading states
		loading,
		error,
		// Helper functions
		getStockById,
		formatPrice,
		// Utility computed
		hasAvailableStocks,
		noStocksMessage,
		// Actions
		refreshStocks,
		executeQuery,
	}
}

/**
 * Alternative standalone function to get formatted stock options
 * Useful for one-time fetches without reactive updates
 */
export const getFormattedStockOptions = async (
	apolloClient,
	companyId,
	customerId,
	existingPrices = [],
) => {
	const { getStockItemsWithCustomerPrice } = await import(
		'../services/getStockItemsWithCustomerPriceService'
	)

	try {
		const { stocks, customerPriceClass } = await getStockItemsWithCustomerPrice(
			apolloClient,
			companyId,
			customerId,
		)

		// Get list of existing stock IDs
		const existingStockIds = new Set()
		existingPrices.forEach((price) => {
			if (price.stock && price.stock.stock_id) {
				existingStockIds.add(price.stock.stock_id)
			}
		})

		// Format price helper
		const formatPrice = (price) => {
			if (!price && price !== 0) return '0.00'
			return new Intl.NumberFormat('sv-SE', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(price / 100)
		}

		// Format and filter stocks
		const formattedOptions = stocks
			.filter((stock) => !existingStockIds.has(stock.stock_id))
			.map((stock) => ({
				...stock,
				displayText: `${stock.stock_id} - ${stock.stock_name} - ${formatPrice(
					stock.customerPrice,
				)}`,
				formattedPrice: formatPrice(stock.customerPrice),
			}))
			.sort((a, b) => a.stock_id.localeCompare(b.stock_id))

		return {
			options: formattedOptions,
			customerPriceClass,
		}
	} catch (error) {
		console.error('Error getting formatted stock options:', error)
		throw error
	}
}
